import { createContext, useCallback, useContext, useMemo, useState } from "react";
import useMediaQuery from "./useMediaQuery";

export const LAYOUT_MODE_KEY = "mysolon.layoutMode";
export const LAYOUT_PREFS = ["desktop", "classic"];

// Desktop is opt-in. When chosen, it applies only at or above this width;
// narrower windows (mobile) show the classic layout until widened again.
export const DESKTOP_MIN_QUERY = "(min-width: 1024px)";

const readPref = () => {
  try {
    const value = localStorage.getItem(LAYOUT_MODE_KEY);
    return LAYOUT_PREFS.includes(value) ? value : "classic";
  } catch {
    return "classic";
  }
};

const LayoutModeContext = createContext({
  mode: "classic",
  isDesktop: false,
  isDesktopWidth: false,
  pref: "classic",
  setPref: () => {},
});

export const LayoutModeProvider = ({ children }) => {
  const [pref, setPrefState] = useState(readPref);
  const isDesktopWidth = useMediaQuery(DESKTOP_MIN_QUERY);

  const setPref = useCallback((value) => {
    if (!LAYOUT_PREFS.includes(value)) return;
    setPrefState(value);
    try {
      localStorage.setItem(LAYOUT_MODE_KEY, value);
    } catch {}
  }, []);

  const mode = pref === "desktop" && isDesktopWidth ? "desktop" : "classic";

  const value = useMemo(
    () => ({
      mode,
      isDesktop: mode === "desktop",
      isDesktopWidth,
      pref,
      setPref,
    }),
    [mode, isDesktopWidth, pref, setPref]
  );

  return (
    <LayoutModeContext.Provider value={value}>
      {children}
    </LayoutModeContext.Provider>
  );
};

export const useLayoutMode = () => useContext(LayoutModeContext);

export default LayoutModeContext;
